import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";

import TrainingSessionResultsDialog from "../../components/TrainingSessionResultsDialog";
import ExerciseResultDialog from "../../components/ExerciseResultDialog";
import { TePipelineDebugPanel } from "../../components/TePipelineDebugPanel";
import { FinalizedExerciseSeatingDialog } from "./FinalizedExerciseSeatingDialog";
import {
  exdetails,
  tablelist,
  grpplayerlist,
  grpuserlist,
  mobileGroupPlayers,
  mobileSessionDetails,
  groupdetails,
  sessionexlist,
} from "../../components/funtionsBD/Mainservice";
import {
  buildPlayerIndexes,
  fetchCoachSessionRoster,
  readGrIdFromStorage,
  resolveClubIdCandidates,
  resolveTableGrId,
} from "../../utils/sessionResultsPlayers";
import { readActiveHomeRole, isPlayerAccount } from "../../utils/homeRole";
import { isPlayerPresentInTrainingSession } from "../../utils/trainingRoomAccess";
import { supportsPlayerPerformanceAutoeval } from "../../utils/teExecutionType";
import { userCanEditFinalizedExerciseSeating } from "../../utils/planningAccess";

export function FinalizedSessionResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const userStorageRaw = localStorage.getItem("user") || "{}";
  const user = useMemo(() => {
    try {
      return JSON.parse(userStorageRaw);
    } catch {
      return {};
    }
  }, [userStorageRaw]);

  const homeRole = useMemo(() => readActiveHomeRole(user), [userStorageRaw]);
  const isCoachSessionView = useMemo(
    () =>
      !isPlayerAccount(user, { homeRole }) &&
      (homeRole === "coach" ||
        homeRole === "admin" ||
        homeRole === "moderator" ||
        homeRole === "monitor"),
    [userStorageRaw, homeRole]
  );

  const session = useMemo(() => {
    const fromState = location.state?.session;
    if (fromState?.ts_id) return fromState;
    const fromStore = JSON.parse(localStorage.getItem("rsessn") || "null");
    return fromStore?.ts_id ? fromStore : null;
  }, [location.state?.session]);

  const selectedgrp = useMemo(() => {
    const fromState = location.state?.selectedgrp;
    if (fromState?.groupDetails) return fromState;
    return JSON.parse(localStorage.getItem("grpinf") || "null");
  }, [location.state?.selectedgrp]);

  const resolvedGrId = useMemo(
    () =>
      selectedgrp?.groupDetails?.gr_id ||
      session?.gr_id ||
      session?._groupId ||
      session?.groupDetails?.gr_id ||
      session?.tbl_group?.gr_id ||
      readGrIdFromStorage() ||
      null,
    [selectedgrp, session]
  );

  const clubIdKey = useMemo(() => {
    const ids = resolveClubIdCandidates(user, session, selectedgrp);
    return ids.join("|");
  }, [userStorageRaw, session?.ts_id, session?.club_id, selectedgrp]);

  const [filterPlayers, setFilterPlayers] = useState([]);
  const [playerByKey, setPlayerByKey] = useState(new Map());
  const [playersLoading, setPlayersLoading] = useState(true);
  const [selectedPlayerId, setSelectedPlayerId] = useState("all");
  const [exerciseOpen, setExerciseOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [exerciseInitialTab, setExerciseInitialTab] = useState("perf");
  const [viewerParticipatedInSession, setViewerParticipatedInSession] = useState(true);
  const [seatingExercise, setSeatingExercise] = useState(null);
  const canEditFinalizedTables = userCanEditFinalizedExerciseSeating(user);

  const localeCode = (localStorage.getItem("i18nextLng") || "en").split("-")[0];
  const tsId = session?.ts_id;

  const selectedPlayer = useMemo(() => {
    if (!selectedPlayerId || selectedPlayerId === "all") return null;
    return playerByKey.get(String(selectedPlayerId)) || null;
  }, [playerByKey, selectedPlayerId]);

  useEffect(() => {
    let cancelled = false;

    const applyRoster = (rosterRaw) => {
      const { byKey, list } = buildPlayerIndexes(rosterRaw);
      setFilterPlayers(list);
      setPlayerByKey(byKey);
      if (list.length > 0) {
        setSelectedPlayerId((prev) => {
          if (prev && prev !== "all" && byKey.has(String(prev))) return prev;
          return list[0]._filterKey || list[0].player_id || list[0].user_id || "all";
        });
      }
    };

    const run = async () => {
      if (!tsId || !isCoachSessionView) {
        setFilterPlayers([]);
        setPlayerByKey(new Map());
        setPlayersLoading(false);
        return;
      }

      setPlayersLoading(true);
      const initialGrId = resolveTableGrId(session, resolvedGrId, selectedgrp) || null;

      try {
        const clubCandidates = resolveClubIdCandidates(user, session, selectedgrp);
        const { rosterRaw, grId } = await fetchCoachSessionRoster({
          tsId,
          grId: initialGrId,
          clubCandidates,
          sessionexlist,
          tablelist,
          mobileSessionDetails,
          mobileGroupPlayers,
          grpplayerlist,
          grpuserlist,
          groupdetails,
          exdetails,
        });

        if (cancelled) return;
        applyRoster(rosterRaw);
      } catch {
        if (!cancelled) {
          setFilterPlayers([]);
          setPlayerByKey(new Map());
        }
      } finally {
        if (!cancelled) setPlayersLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [tsId, resolvedGrId, clubIdKey, isCoachSessionView]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (isCoachSessionView || !tsId) {
        if (!cancelled) setViewerParticipatedInSession(true);
        return;
      }

      const grId =
        resolvedGrId ||
        session?.gr_id ||
        session?._groupId ||
        readGrIdFromStorage() ||
        null;
      if (!grId || !user?.user_id) {
        if (!cancelled) setViewerParticipatedInSession(false);
        return;
      }

      try {
        const res = await grpplayerlist({ ts_id: tsId, gr_id: grId });
        const roster = res?.data?.data || [];
        const me = roster.find(
          (p) => p?.user_id != null && String(p.user_id) === String(user.user_id)
        );
        if (!cancelled) {
          setViewerParticipatedInSession(isPlayerPresentInTrainingSession(me));
        }
      } catch {
        if (!cancelled) setViewerParticipatedInSession(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isCoachSessionView, tsId, resolvedGrId, session?.gr_id, session?._groupId, user?.user_id]);

  const handleSelectExercise = async (ex, opts) => {
    const rosterEval = supportsPlayerPerformanceAutoeval(ex);
    // Players always view their own results; only the coach roster needs a pick.
    const needsPlayerPick =
      isCoachSessionView && !rosterEval && (selectedPlayerId === "all" || !selectedPlayer);
    if (!ex || needsPlayerPick) return;
    setExerciseInitialTab(opts?.initialTab || (rosterEval ? "autoeval" : "perf"));

    const keys = [selectedPlayer?.player_id, selectedPlayer?.user_id]
      .filter((v) => v != null)
      .map(String);

    try {
      const detailRes = await exdetails({ te_id: ex.te_id });
      const detail = detailRes?.data?.data || {};
      const playerDetails = Array.isArray(detail.player_details) ? detail.player_details : [];
      const record = playerDetails.find(
        (p) =>
          keys.includes(String(p.player_id)) || keys.includes(String(p.user_id))
      );
      const autoData = record?.auto_evaluation_data;
      const autoEval =
        autoData?.exercise_perf != null || autoData?.perceived_exertion != null
          ? autoData
          : autoData?.evaluation_data || null;

      setSelectedExercise({
        ...ex,
        type_ex: detail.type_ex || ex.type_ex,
        sub_type_ex: detail.sub_type_ex || ex.sub_type_ex,
        sequences: detail.sequences || ex.sequences,
        autoEval,
      });
    } catch {
      setSelectedExercise(ex);
    }
    setExerciseOpen(true);
  };

  const handleClose = () => {
    if (selectedgrp) {
      localStorage.setItem("grpinf", JSON.stringify(selectedgrp));
    }
    if (session) {
      localStorage.setItem("rsessn", JSON.stringify(session));
    }
    navigate("/home", {
      state: {
        focusSession: session,
        focusGroup: selectedgrp,
      },
    });
  };

  if (!session?.ts_id) {
    return null;
  }

  return (
    <Box
      sx={{
        height: "100dvh",
        maxHeight: "100dvh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "var(--bg-color)",
        color: "var(--text-color)",
      }}
    >
      <TePipelineDebugPanel tsId={tsId} focusTeId={selectedExercise?.te_id || null} />

      <TrainingSessionResultsDialog
        open
        pageMode
        coachMode={isCoachSessionView}
        session={session}
        localeCode={localeCode}
        onClose={handleClose}
        filterPlayers={filterPlayers}
        playersLoading={playersLoading}
        selectedPlayerId={selectedPlayerId}
        onPlayerSelect={setSelectedPlayerId}
        onSelectExercise={handleSelectExercise}
        viewerParticipatedInSession={viewerParticipatedInSession}
        onManageTables={
          canEditFinalizedTables
            ? (ex) => setSeatingExercise(ex)
            : null
        }
      />

      <FinalizedExerciseSeatingDialog
        open={Boolean(seatingExercise?.te_id)}
        onClose={() => setSeatingExercise(null)}
        session={session}
        selectedgrp={selectedgrp}
        grId={resolvedGrId}
        exercise={seatingExercise}
        roster={filterPlayers}
        user={user}
      />

      {selectedExercise && (
        <ExerciseResultDialog
          open={exerciseOpen}
          onClose={() => {
            setExerciseOpen(false);
            setSelectedExercise(null);
          }}
          onBack={() => {
            setExerciseOpen(false);
            setSelectedExercise(null);
          }}
          exercise={selectedExercise}
          initialTab={exerciseInitialTab}
          session={session}
          onNavigateExercise={setSelectedExercise}
          viewPlayerId={
            selectedPlayer?.player_id != null ? String(selectedPlayer.player_id) : null
          }
          viewUserId={
            selectedPlayer?.user_id != null
              ? String(selectedPlayer.user_id)
              : selectedPlayer?.id != null
                ? String(selectedPlayer.id)
                : null
          }
          localeCode={localeCode}
          viewerParticipatedInSession={viewerParticipatedInSession}
          onExerciseCatalogChanged={(data) => {
            setSelectedExercise((prev) => {
              if (!prev || !data?.te_id || String(prev.te_id) !== String(data.te_id)) {
                return prev;
              }
              if (data.scope === "all" && data.exercise) {
                return {
                  ...prev,
                  ex_id: data.ex_id || data.exercise.ex_id || prev.ex_id,
                  ...data.exercise,
                };
              }
              const pid = data.player_id ? String(data.player_id) : null;
              if (!pid || !data.exercise) return prev;
              return {
                ...prev,
                player_ex_id_overrides: {
                  ...(prev.player_ex_id_overrides || {}),
                  [pid]: data.ex_id,
                },
                player_ex_override_details: {
                  ...(prev.player_ex_override_details || {}),
                  [pid]: data.exercise,
                },
              };
            });
          }}
        />
      )}
    </Box>
  );
}
